package com.evoapps.evoshift.ui.user
import androidx.compose.ui.semantics.selected
import androidx.compose.ui.semantics.semantics
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.foundation.clickable

import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.EventNote
import androidx.compose.material.icons.filled.Remove
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.RadioButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.widgets.IconButtonWithTooltip
import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.AuditMetadata
import com.evoapps.evoshift.domain.model.CareRecipient
import com.evoapps.evoshift.domain.model.LeaveBalance
import com.evoapps.evoshift.domain.model.LimitUnit
import com.evoapps.evoshift.domain.model.OccasionReason
import com.evoapps.evoshift.domain.model.RequestPlace
import com.evoapps.evoshift.domain.model.WorkRequest
import com.evoapps.evoshift.domain.model.WorkRequestKind
import com.evoapps.evoshift.domain.requests.LimitCheck
import com.evoapps.evoshift.domain.requests.RequestCatalog
import com.evoapps.evoshift.domain.requests.RequestCounter
import com.evoapps.evoshift.domain.requests.RequestCounters
import com.evoapps.evoshift.domain.requests.RequestGroup
import com.evoapps.evoshift.domain.requests.RequestType
import com.evoapps.evoshift.domain.requests.StatutoryHint
import com.evoapps.evoshift.domain.usecase.ReportAbsenceUseCase
import com.evoapps.evoshift.domain.usecase.requests.WorkPlacesUseCase
import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.util.label
import com.evoapps.evoshift.viewmodels.AbsencesUiState
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogSection
import com.evoapps.evoshift.widgets.EvoDateField
import com.evoapps.evoshift.widgets.EvoDateRangeField
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoRadioGroup
import com.evoapps.evoshift.widgets.EvoSwitchRow
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.EvoTimeField
import com.evoapps.evoshift.widgets.LocalOnColours
import com.evoapps.evoshift.widgets.MissingYear
import com.evoapps.evoshift.widgets.parseClockTime
import com.evoapps.evoshift.widgets.parseIsoDate
import evoshift.composeapp.generated.resources.*
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus
import org.jetbrains.compose.resources.stringResource

/** SH-223: what the new-request form sends: an absence of any type, or a work request. */
sealed interface NewRequest {
    /** [approveNow]: a manager filing it for someone approves it at once ("Approve now"). */
    data class Leave(val draft: Absence, val approveNow: Boolean = false) : NewRequest
    data class Work(val draft: WorkRequest, val approveNow: Boolean = false) : NewRequest
}

/** Test tags of the new-request form. */
object NewRequestTags {
    const val TYPE_CHOOSER = "new_request_types"
    /** The field that shows the chosen type and opens the list. */
    const val TYPE_FIELD = "new_request_type_field"
    const val COUNTER = "new_request_counter"
    const val PLACES = "new_request_places"
    const val LIMIT_ERROR = "new_request_limit_error"
    fun type(name: String) = "new_request_type_$name"
    fun place(id: String) = "new_request_place_$id"
}


/**
 * SH-223: "New request": the type (grouped: leave and time off, family and care, health, work),
 * then what that type needs: the dates, or a day and hours where the type allows them; the
 * occasion, whom the care is for, the number of children; the place to work from (the workplace's
 * shared list, the person's recent places first, and a new one added on the spot). Each type
 * says who pays for the time, the statutory lengths are hints, and the yearly limits show what is
 * left; a request over a limit is refused here with the reason (a manager can still approve one).
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun NewRequestDialog(
    state: AbsencesUiState,
    /** SH-113: the person's leave balance, for annual leave and leave on demand. */
    balance: LeaveBalance? = null,
    onAddPlace: (String) -> Unit = {},
    onAddedPlaceConsumed: () -> Unit = {},
    /** SH-224: the type chosen first (the timeline's lane it is added from), when offered. */
    initialType: RequestType? = null,
    onDismiss: () -> Unit,
    onConfirm: (NewRequest) -> Unit,
    /** SH-223: the name of whom a manager fills the form in for (null: the person's own request). */
    forName: String? = null,
    /** SH-223: also offer "Approve now" (a manager with APPROVE_REQUESTS filling it in for someone). */
    canApproveNow: Boolean = false
) {
    // SH-223: the types offered today (the workplace's rules and the person's contract); the first is annual leave where offered.
    val offeredToday = state.offeredTypes(AppClock.today().toString())
    var type by remember {
        mutableStateOf(initialType?.takeIf { wanted -> offeredToday.any { it.type == wanted } }
            ?: offeredToday.firstOrNull { it.type == RequestType.Leave(AbsenceType.VACATION) }?.type ?: offeredToday.firstOrNull()?.type
            ?: RequestType.Leave(AbsenceType.VACATION))
    }
    var sickPayFull by remember { mutableStateOf(false) }
    var start by remember { mutableStateOf("") }
    var end by remember { mutableStateOf("") }
    var hourly by remember { mutableStateOf(false) }
    var fromTime by remember { mutableStateOf("") }
    var toTime by remember { mutableStateOf("") }
    var chosenUnit by remember { mutableStateOf<LimitUnit?>(null) }
    var occasion by remember { mutableStateOf<OccasionReason?>(null) }
    var careFor by remember { mutableStateOf(CareRecipient.CHILD) }
    var children by remember { mutableStateOf(1) }
    var placeId by remember { mutableStateOf<String?>(null) }
    var reason by remember { mutableStateOf("") }
    var tried by remember { mutableStateOf(false) }
    var picking by remember { mutableStateOf(false) }

    val info = when (val t = type) {
        is RequestType.Leave -> RequestCatalog.of(t.type)
        is RequestType.Work -> RequestCatalog.of(t.kind)
    }
    val leaveType = (type as? RequestType.Leave)?.type
    val workKind = (type as? RequestType.Work)?.kind

    // The new place added from here is the one chosen.
    LaunchedEffect(state.addedPlace) {
        state.addedPlace?.let {
            placeId = it.id
            onAddedPlaceConsumed()
        }
    }

    val startDate = parseIsoDate(start)
    val year = startDate?.year ?: AppClock.today().year
    // SH-223: offered on the request's day (a contract may change); the chooser lists those.
    val offered = state.offeredTypes((startDate ?: AppClock.today()).toString())
    val notOffered = offered.none { it.type == type }
    // Childcare and force majeure: the year's unit, set by its first request; else the one chosen here.
    val limitedInEither = leaveType == AbsenceType.CHILDCARE || leaveType == AbsenceType.FORCE_MAJEURE
    val fixedUnit = if (limitedInEither) state.yearUnit(leaveType!!, year) else null
    val unit = fixedUnit ?: chosenUnit ?: LimitUnit.DAYS
    val hoursAllowed = info.allowsHours && (!limitedInEither || unit == LimitUnit.HOURS)
    val inHours = info.requiresHours || (hoursAllowed && hourly)
    val bloodDays = state.requestSettings.clamped().bloodDonationDays
    val endDate = when {
        inHours -> startDate
        leaveType == AbsenceType.BLOOD_DONATION -> startDate?.plus(DatePeriod(days = bloodDays - 1))
        else -> parseIsoDate(end)
    }
    val endBeforeStart = startDate != null && endDate != null && endDate < startDate
    val timesValid = !inHours || (parseClockTime(fromTime) != null && parseClockTime(toTime) != null && fromTime != toTime)
    val placeName = state.places.firstOrNull { it.id == placeId }?.name
    val placeMissing = workKind == WorkRequestKind.REMOTE_WORK && placeName == null
    val note = reason.trim().takeIf { it.isNotBlank() }

    val draft: Absence? = leaveType?.let { t ->
        if (startDate == null || endDate == null) null
        else Absence(
            id = "", userId = state.userId, workplaceId = state.workplaceId, type = t, status = AbsenceStatus.PENDING,
            startDate = startDate.toString(), endDate = endDate.toString(), reason = note, attachmentUrl = null,
            processedBy = null, processedAt = null, metadata = AuditMetadata(createdAt = "", createdBy = state.userId),
            sickPayFull = sickPayFull && (t == AbsenceType.SICK_LEAVE || t == AbsenceType.QUARANTINE),
            startTime = parseClockTime(fromTime).takeIf { inHours }, endTime = parseClockTime(toTime).takeIf { inHours },
            occasion = occasion.takeIf { t == AbsenceType.OCCASIONAL_LEAVE },
            careFor = careFor.takeIf { t == AbsenceType.CARE_ALLOWANCE },
            limitUnit = unit.takeIf { limitedInEither },
            children = children.takeIf { t == AbsenceType.MATERNITY || t == AbsenceType.PARENTAL }
        )
    }
    val limit = draft?.takeIf { timesValid && !endBeforeStart }?.let { state.check(it) } ?: LimitCheck.Ok
    val valid = startDate != null && endDate != null && !endBeforeStart && timesValid && !placeMissing && limit == LimitCheck.Ok && !notOffered

    fun send(approveNow: Boolean) {

                tried = true
                if (valid) {
                    if (draft != null) onConfirm(NewRequest.Leave(draft, approveNow))
                    else if (workKind != null) onConfirm(
                        NewRequest.Work(
                            WorkRequest(
                                id = "", userId = state.userId, workplaceId = state.workplaceId.orEmpty(), kind = workKind,
                                status = AbsenceStatus.PENDING, startDate = startDate.toString(), endDate = endDate.toString(),
                                startTime = parseClockTime(fromTime).takeIf { inHours }, endTime = parseClockTime(toTime).takeIf { inHours },
                                placeId = placeId.takeIf { placeName != null }, placeName = placeName, note = note,
                                metadata = AuditMetadata(createdAt = "", createdBy = state.userId)
                            ),
                            approveNow
                        )
                    )
                }
    }

    EvoDialog(
        title = stringResource(Res.string.absences_request_new),
        subtitle = forName?.let { stringResource(Res.string.request_for_person, it) },
        icon = Icons.Default.EventNote,
        onDismiss = onDismiss,
        bodyArrangement = Arrangement.spacedBy(24.dp),
        confirm = DialogAction(stringResource(Res.string.absences_action_submit), { send(false) }),
        neutral = if (canApproveNow) DialogAction(stringResource(Res.string.request_approve_now), { send(true) }) else null
    ) {
        // The dialog's colours (light on navy): read inside its surface.
        val on = LocalOnColours.current
        DialogSection(stringResource(Res.string.absences_type_label)) {
            // One tap opens the list of the offered types, one tap chooses.
            RequestTypeField(typeLabel(type), notOffered, onClick = { picking = true })
            Text(stringResource(info.payRes()), style = MaterialTheme.typography.bodyMedium, color = on.onSurface, fontWeight = FontWeight.SemiBold)
            if (!info.blocksScheduling) Text(forName?.let { stringResource(Res.string.request_blocks_no_for, it) } ?: stringResource(Res.string.request_blocks_no), style = MaterialTheme.typography.bodySmall, color = on.onSurfaceVariant)
            if (leaveType == AbsenceType.SICK_LEAVE || leaveType == AbsenceType.QUARANTINE) {
                EvoSwitchRow(sickPayFull, { sickPayFull = it }, stringResource(Res.string.absences_sick_pay_full))
            }
        }

        TypeDetails(state, leaveType, year, unit, fixedUnit, careFor, children, bloodDays, occasion, forName,
            onUnit = { chosenUnit = it; if (it == LimitUnit.DAYS) hourly = false },
            onCareFor = { careFor = it }, onChildren = { children = it }, onOccasion = { occasion = it })

        DialogSection(stringResource(Res.string.absences_period)) {
            if (hoursAllowed && !info.requiresHours) {
                EvoSwitchRow(hourly, { hourly = it }, stringResource(Res.string.request_part_of_day))
            }
            if (inHours || leaveType == AbsenceType.BLOOD_DONATION) {
                EvoDateField(
                    value = start,
                    onValueChange = { start = it },
                    label = stringResource(if (leaveType == AbsenceType.BLOOD_DONATION) Res.string.request_blood_date else Res.string.request_date),
                    showErrors = tried,
                    missingYear = MissingYear.Upcoming
                )
                if (inHours) {
                    Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        EvoTimeField(fromTime, { fromTime = it }, stringResource(Res.string.request_start_time), Modifier.weight(1f), showErrors = tried)
                        EvoTimeField(toTime, { toTime = it }, stringResource(Res.string.request_end_time), Modifier.weight(1f), showErrors = tried,
                            imeAction = ImeAction.Done)
                    }
                }
            } else {
                // SH-166: typed ("3.11") or picked together in the range picker; a sick leave is
                // usually reported afterwards, a leave ahead (a missing year is the next one then).
                EvoDateRangeField(
                    start = start,
                    end = end,
                    onStartChange = { start = it },
                    onEndChange = { end = it },
                    startLabel = stringResource(Res.string.absences_start_date),
                    endLabel = stringResource(Res.string.absences_end_date),
                    pickerTitle = stringResource(Res.string.absences_period),
                    missingYear = if (leaveType == AbsenceType.SICK_LEAVE || leaveType == AbsenceType.QUARANTINE) MissingYear.ThisYear else MissingYear.Upcoming,
                    showErrors = tried,
                    endError = stringResource(Res.string.date_end_before_start).takeIf { endBeforeStart }
                )
            }
            if (leaveType != null) balance?.let { LeaveRequestCheck(it, leaveType, startDate, endDate) }
            if (notOffered) Text(forName?.let { stringResource(Res.string.request_not_offered_for, it) } ?: stringResource(Res.string.request_not_offered), style = MaterialTheme.typography.bodyMedium, color = on.error)
            limit.toUiText(forName)?.let { message ->
                Row(Modifier.testTag(NewRequestTags.LIMIT_ERROR), verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Default.Warning, contentDescription = null, tint = on.error, modifier = Modifier.padding(end = 6.dp))
                    Text(message.asString(), style = MaterialTheme.typography.bodyMedium, color = on.error)
                }
            }
        }

        if (picking) {
            RequestTypePicker(
                state = state,
                ordered = state.pickerOrder((startDate ?: AppClock.today()).toString()),
                year = year,
                selected = type,
                onPick = { option ->
                    type = option.type
                    hourly = option.requiresHours
                    picking = false
                },
                onDismiss = { picking = false }
            )
        }

        if (info.asksPlace) {
            DialogSection(stringResource(if (workKind == WorkRequestKind.REMOTE_WORK) Res.string.request_place_label else Res.string.request_place_optional)) {
                WorkPlacePicker(
                    recentLabel = forName?.let { stringResource(Res.string.place_recent_for, it) },
                    places = state.orderedPlaces,
                    recentCount = state.recentPlaceCount,
                    selectedId = placeId,
                    onSelect = { placeId = if (placeId == it && workKind != WorkRequestKind.REMOTE_WORK) null else it },
                    onAdd = onAddPlace
                )
                if (tried && placeMissing) Text(stringResource(Res.string.place_required), style = MaterialTheme.typography.bodyMedium, color = on.error)
            }
        }

        DialogSection(null) {
            EvoTextField(
                value = reason,
                // Free text next to possibly health data: short, and no diagnoses (art. 22^1 KP, SH-50).
                onValueChange = { reason = it.take(ReportAbsenceUseCase.REASON_MAX_LENGTH) },
                label = stringResource(Res.string.absences_reason_hint),
                supportingText = stringResource(Res.string.absences_reason_privacy),
                singleLine = false,
                minLines = 2,
                imeAction = ImeAction.Default
            )
        }
    }
}

/** What the chosen type needs beyond its dates: its unit and counter, the occasion, whom for, the children; and its hints. */
@Composable
private fun TypeDetails(
    state: AbsencesUiState,
    type: AbsenceType?,
    year: Int,
    unit: LimitUnit,
    fixedUnit: LimitUnit?,
    careFor: CareRecipient,
    children: Int,
    bloodDays: Int,
    occasion: OccasionReason?,
    forName: String?,
    onUnit: (LimitUnit) -> Unit,
    onCareFor: (CareRecipient) -> Unit,
    onChildren: (Int) -> Unit,
    onOccasion: (OccasionReason) -> Unit
) {
    val on = LocalOnColours.current
    val settings = state.requestSettings.clamped()
    @Composable
    fun hint(text: String) = Text(text, style = MaterialTheme.typography.bodyMedium, color = on.onSurfaceVariant)
    when (type) {
        AbsenceType.CHILDCARE, AbsenceType.FORCE_MAJEURE -> DialogSection(stringResource(Res.string.request_unit_title)) {
            val (days, hours) = if (type == AbsenceType.CHILDCARE) settings.childcareDays to settings.childcareHours
            else settings.forceMajeureDays to settings.forceMajeureHours
            if (fixedUnit == null) {
                EvoRadioGroup(
                    options = LimitUnit.entries,
                    selected = unit,
                    onSelect = onUnit,
                    optionLabel = { if (it == LimitUnit.DAYS) stringResource(Res.string.request_unit_days, days) else stringResource(Res.string.request_unit_hours, hours) }
                )
                hint(stringResource(Res.string.request_unit_note))
            } else {
                hint(stringResource(if (fixedUnit == LimitUnit.DAYS) Res.string.request_unit_fixed_days else Res.string.request_unit_fixed_hours))
            }
            state.counter(type, year, unit)?.let { CounterLines(type.label(), year, it) }
        }
        AbsenceType.CARER_LEAVE -> DialogSection(null) {
            hint(stringResource(Res.string.hint_carer_leave))
            state.counter(type, year)?.let { CounterLines(type.label(), year, it) }
        }
        AbsenceType.TIME_OFF_IN_LIEU -> DialogSection(null) {
            hint(stringResource(Res.string.hint_time_off_in_lieu))
            state.overtimeBalance?.let { OvertimeLines(it, forName) }
        }
        AbsenceType.CARE_ALLOWANCE -> DialogSection(stringResource(Res.string.care_for_label)) {
            EvoRadioGroup(
                options = CareRecipient.entries,
                selected = careFor,
                onSelect = onCareFor,
                optionLabel = { stringResource(if (it == CareRecipient.CHILD) Res.string.care_for_child else Res.string.care_for_family) }
            )
            Column(Modifier.testTag(NewRequestTags.COUNTER), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                state.careCounters(year).forEach { counter ->
                    val res = when (counter.careFor) {
                        CareRecipient.CHILD -> Res.string.care_counter_child
                        CareRecipient.FAMILY -> Res.string.care_counter_family
                        null -> Res.string.care_counter_total
                    }
                    Text(stringResource(res, amountText(counter.used + counter.pending), amountText(counter.limit)), style = MaterialTheme.typography.bodyMedium, color = on.onSurface)
                }
                hint(stringResource(Res.string.counter_hint_only))
            }
        }
        AbsenceType.OCCASIONAL_LEAVE -> DialogSection(stringResource(Res.string.occasion_label)) {
            EvoRadioGroup(options = OccasionReason.entries, selected = occasion, onSelect = onOccasion, optionLabel = { stringResource(it.labelRes()) })
            hint(stringResource(Res.string.occasion_proof))
        }
        AbsenceType.MATERNITY, AbsenceType.PARENTAL -> DialogSection(stringResource(Res.string.children_label)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                IconButton(onClick = { onChildren((children - 1).coerceAtLeast(1)) }, enabled = children > 1) {
                    Icon(Icons.Default.Remove, contentDescription = null, tint = on.onSurface)
                }
                Text(children.toString(), style = MaterialTheme.typography.titleMedium, color = on.onSurface, modifier = Modifier.padding(horizontal = 8.dp))
                IconButtonWithTooltip(onClick = { onChildren((children + 1).coerceAtMost(10)) }, tooltip = stringResource(Res.string.children_label)) {
                    Icon(Icons.Default.Add, contentDescription = stringResource(Res.string.children_label), tint = on.onSurface)
                }
            }
            (StatutoryHint.of(type, children, settings) as? StatutoryHint.Weeks)?.let { hint(stringResource(Res.string.hint_weeks, it.weeks)) }
        }
        AbsenceType.PATERNITY -> DialogSection(null) { hint(stringResource(Res.string.hint_paternity)) }
        AbsenceType.CHILDCARE_LEAVE -> DialogSection(null) { hint(stringResource(Res.string.hint_childcare_leave)) }
        AbsenceType.BLOOD_DONATION -> DialogSection(null) { hint(stringResource(Res.string.hint_blood_donation, bloodDays)) }
        else -> {}
    }
}

/** A yearly counter in the dialog's colours: "Childcare in 2026", "1 of 2 days left", what waits. */
@Composable
fun CounterLines(title: String, year: Int, counter: RequestCounter) {
    val on = LocalOnColours.current
    Column(Modifier.fillMaxWidth().testTag(NewRequestTags.COUNTER), verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Text(stringResource(Res.string.counter_title, title, year), style = MaterialTheme.typography.labelLarge, color = on.onSurfaceVariant)
        Text(counterLeftText(counter), style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.Bold, color = on.onSurface)
        if (counter.pending > 0) Text(stringResource(Res.string.counter_pending, amountText(counter.pending)), style = MaterialTheme.typography.bodySmall, color = on.onSurfaceVariant)
    }
}

@Composable
fun counterLeftText(counter: RequestCounter): String =
    if (counter.unit == LimitUnit.HOURS) stringResource(Res.string.counter_hours_left, amountText(counter.remaining), amountText(counter.limit))
    else stringResource(Res.string.counter_days_left, amountText(counter.remaining), amountText(counter.limit))

@Composable
private fun OvertimeLines(balance: RequestCounter, forName: String? = null) {
    val on = LocalOnColours.current
    Column(Modifier.fillMaxWidth().testTag(NewRequestTags.COUNTER), verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Text(stringResource(Res.string.counter_overtime_title), style = MaterialTheme.typography.labelLarge, color = on.onSurfaceVariant)
        Text(
            stringResource(Res.string.counter_overtime_left, amountText(balance.remaining), amountText(balance.limit)),
            style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.Bold, color = on.onSurface
        )
        Text(stringResource(if (forName != null) Res.string.counter_overtime_ratio_for else Res.string.counter_overtime_ratio), style = MaterialTheme.typography.bodySmall, color = on.onSurfaceVariant)
    }
}

@Composable
private fun typeLabel(type: RequestType): String = when (type) {
    is RequestType.Leave -> type.type.label()
    is RequestType.Work -> type.kind.label()
}

/** The chosen type, as a field: tapping it opens the list ([RequestTypePicker]). */
@Composable
private fun RequestTypeField(label: String, notOffered: Boolean, onClick: () -> Unit) {
    val on = LocalOnColours.current
    val shape = RoundedCornerShape(12.dp)
    val choose = stringResource(Res.string.request_choose_type)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .testTag(NewRequestTags.TYPE_FIELD)
            .clip(shape)
            .border(1.dp, if (notOffered) on.error else on.onSurfaceVariant, shape)
            .handCursor()
            .clickable(role = Role.DropdownList, onClickLabel = choose, onClick = onClick)
            .heightIn(min = 56.dp)
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(label, style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.SemiBold, color = on.onSurface, modifier = Modifier.weight(1f))
        Icon(Icons.Default.ArrowDropDown, contentDescription = null, tint = DarkGold)
    }
}

/**
 * SH-223: the list of the types offered (the workplace's rules, the person's contract that day):
 * the most used on top, "Others" below. Each row its name and, where useful, a short line: what
 * is left of a yearly limit, half pay, ZUS or unpaid. A bottom sheet on phones, a dialog on wide
 * windows.
 */
@OptIn(androidx.compose.material3.ExperimentalMaterial3Api::class)
@Composable
private fun RequestTypePicker(
    state: AbsencesUiState,
    ordered: com.evoapps.evoshift.domain.requests.RequestOrdering.Ordered,
    year: Int,
    selected: RequestType,
    onPick: (com.evoapps.evoshift.domain.requests.RequestTypeInfo) -> Unit,
    onDismiss: () -> Unit
) {
    val title = stringResource(Res.string.request_choose_type)
    val rows: @Composable ColumnScope.() -> Unit = {
        val on = LocalOnColours.current
        Column(Modifier.fillMaxWidth().testTag(NewRequestTags.TYPE_CHOOSER), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            if (ordered.top.isNotEmpty()) Text(stringResource(Res.string.request_popular), style = MaterialTheme.typography.labelLarge, color = on.onSurfaceVariant)
            ordered.top.forEach { TypeRow(state, it, year, it.type == selected) { onPick(it) } }
            if (ordered.rest.isNotEmpty()) {
                HorizontalDivider(color = on.onSurfaceVariant.copy(alpha = 0.4f), modifier = Modifier.padding(vertical = 4.dp))
                Text(stringResource(Res.string.request_others), style = MaterialTheme.typography.labelLarge, color = on.onSurfaceVariant)
            }
            ordered.rest.forEach { TypeRow(state, it, year, it.type == selected) { onPick(it) } }
        }
    }
    if (com.evoapps.evoshift.ui.adaptive.LocalWindowWidthClass.current.isExpanded) {
        EvoDialog(title = title, icon = Icons.Default.EventNote, onDismiss = onDismiss, width = com.evoapps.evoshift.widgets.DialogWidth.Compact,
            bodyArrangement = Arrangement.spacedBy(8.dp), content = rows)
    } else {
        com.evoapps.evoshift.widgets.EvoBottomSheet(onDismiss = onDismiss, title = title, icon = Icons.Default.EventNote, content = rows)
    }
}

/** One type in the list: its name, the short line, a tick when it is the chosen one. */
@Composable
private fun TypeRow(state: AbsencesUiState, info: com.evoapps.evoshift.domain.requests.RequestTypeInfo, year: Int, selected: Boolean, onClick: () -> Unit) {
    com.evoapps.evoshift.widgets.EvoListRow(
        headline = typeLabel(info.type),
        onClick = onClick,
        modifier = Modifier.testTag(NewRequestTags.type(info.type.name)).semantics { this.selected = selected },
        supporting = typeSupport(state, info, year),
        trailing = if (selected) { { Icon(Icons.Default.Check, contentDescription = null, tint = DarkGold) } } else null
    )
}

/** The short line of a type in the list, or null. */
@Composable
private fun typeSupport(state: AbsencesUiState, info: com.evoapps.evoshift.domain.requests.RequestTypeInfo, year: Int): String? {
    val leave = (info.type as? RequestType.Leave)?.type
    val left = when (leave) {
        AbsenceType.CHILDCARE, AbsenceType.FORCE_MAJEURE, AbsenceType.CARER_LEAVE -> state.counter(leave, year)?.let { counterLeftText(it) }
        AbsenceType.TIME_OFF_IN_LIEU -> state.overtimeBalance?.takeIf { it.limit > 0 }?.let {
            stringResource(Res.string.counter_hours_left, amountText(it.remaining), amountText(it.limit))
        }
        AbsenceType.VACATION -> state.leaveBalance?.takeIf { it.isKnown }?.let {
            stringResource(Res.string.leave_balance_left, it.availableDays, it.totalDays)
        }
        else -> null
    }
    val pay = when {
        info.type is RequestType.Work || leave == AbsenceType.SICK_LEAVE || leave == AbsenceType.QUARANTINE -> null
        info.payer == com.evoapps.evoshift.domain.requests.Payer.ZUS -> stringResource(Res.string.request_short_zus)
        // Unpaid leave says so by its name.
        info.payer == com.evoapps.evoshift.domain.requests.Payer.NOBODY && leave != AbsenceType.UNPAID_LEAVE -> stringResource(Res.string.request_short_unpaid)
        info.payShare < 1.0 -> stringResource(Res.string.request_short_half)
        else -> null
    }
    return when {
        left != null && pay != null -> stringResource(Res.string.request_short_line, left, pay)
        else -> left ?: pay
    }
}

/**
 * SH-223: the workplace's shared places, the person's recent ones first ("Your recent places",
 * then "All places"), and a new one typed and added on the spot (an existing name is that place).
 */
@Composable
fun WorkPlacePicker(
    places: List<RequestPlace>,
    recentCount: Int,
    selectedId: String?,
    onSelect: (String) -> Unit,
    onAdd: (String) -> Unit,
    /** SH-223: "Recent places: Jan" when filled in for someone else. */
    recentLabel: String? = null
) {
    val on = LocalOnColours.current
    var newName by remember { mutableStateOf("") }
    Column(Modifier.fillMaxWidth().testTag(NewRequestTags.PLACES), verticalArrangement = Arrangement.spacedBy(4.dp)) {
        if (places.isEmpty()) Text(stringResource(Res.string.place_none), style = MaterialTheme.typography.bodyMedium, color = on.onSurfaceVariant)
        places.forEachIndexed { index, place ->
            if (index == 0 && recentCount > 0) Text(recentLabel ?: stringResource(Res.string.place_recent), style = MaterialTheme.typography.labelLarge, color = on.onSurfaceVariant)
            if (index == recentCount && index > 0) Text(stringResource(Res.string.place_all), style = MaterialTheme.typography.labelLarge, color = on.onSurfaceVariant,
                modifier = Modifier.padding(top = 8.dp))
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .testTag(NewRequestTags.place(place.id))
                    .heightIn(min = 44.dp)
                    .handCursor()
                    .selectable(selected = place.id == selectedId, role = Role.RadioButton, onClick = { onSelect(place.id) }),
                verticalAlignment = Alignment.CenterVertically
            ) {
                RadioButton(
                    selected = place.id == selectedId, onClick = null,
                    colors = RadioButtonDefaults.colors(selectedColor = DarkGold, unselectedColor = on.onSurfaceVariant)
                )
                Spacer(Modifier.width(8.dp))
                Text(place.name, style = MaterialTheme.typography.bodyLarge, color = on.onSurface)
            }
        }
        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(top = 4.dp)) {
            EvoTextField(
                value = newName,
                onValueChange = { newName = it.take(WorkPlacesUseCase.MAX_NAME) },
                label = stringResource(Res.string.place_new_label),
                modifier = Modifier.weight(1f),
                imeAction = ImeAction.Done
            )
            Spacer(Modifier.width(8.dp))
            TextButton(onClick = { if (newName.isNotBlank()) { onAdd(newName.trim()); newName = "" } }, enabled = newName.isNotBlank()) {
                Icon(Icons.Default.Add, contentDescription = null, tint = DarkGold, modifier = Modifier.size(18.dp))
                Spacer(Modifier.width(4.dp))
                Text(stringResource(Res.string.place_add), color = DarkGold, fontWeight = FontWeight.Bold)
            }
        }
    }
}
